import * as XLSX from 'xlsx';

export type CandidateImportRow = {
  full_name: string;
  email: string;
  phone: string | null;
  skills: string[];
  current_company: string | null;
  current_title: string | null;
  location: string | null;
  linkedin_url: string | null;
};

const HEADER_ALIASES: Record<string, keyof CandidateImportRow | 'skip'> = {
  full_name: 'full_name',
  name: 'full_name',
  fullname: 'full_name',
  candidate_name: 'full_name',
  email: 'email',
  emailaddress: 'email',
  mail: 'email',
  phone: 'phone',
  mobile: 'phone',
  skills: 'skills',
  skill: 'skills',
  technical_skills: 'skills',
  current_company: 'current_company',
  company: 'current_company',
  employer: 'current_company',
  current_title: 'current_title',
  title: 'current_title',
  role: 'current_title',
  designation: 'current_title',
  location: 'location',
  city: 'location',
  linkedin: 'linkedin_url',
  linkedin_url: 'linkedin_url',
  linkedin_profile: 'linkedin_url',
};

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function parseSkillsCell(v: unknown): string[] {
  if (v == null) return [];
  const s = String(v).trim();
  if (!s) return [];
  return s.split(/[,;/|]/).map((x) => x.trim()).filter(Boolean);
}

function str(v: unknown): string {
  if (v == null || v === '') return '';
  return String(v).trim();
}

/**
 * Parse first sheet of a .csv, .xlsx, or .xls file into normalized import rows.
 */
export async function parseCandidateSpreadsheet(file: File): Promise<{
  rows: CandidateImportRow[];
  parseErrors: string[];
}> {
  const parseErrors: string[] = [];
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    return { rows: [], parseErrors: ['Workbook has no sheets.'] };
  }
  const sheet = wb.Sheets[sheetName];
  const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  if (!json.length) {
    return { rows: [], parseErrors: ['No data rows after header.'] };
  }

  const firstKeys = Object.keys(json[0]);
  let hasName = false;
  let hasEmail = false;
  for (const k of firstKeys) {
    const m = HEADER_ALIASES[normalizeHeader(k)];
    if (m === 'full_name') hasName = true;
    if (m === 'email') hasEmail = true;
  }
  if (!hasName || !hasEmail) {
    parseErrors.push('Need columns for name (full_name / name) and email.');
  }

  const rows: CandidateImportRow[] = [];
  for (let ri = 0; ri < json.length; ri++) {
    const obj = json[ri];
    const draft: Partial<CandidateImportRow> = { skills: [] };

    for (const [rawKey, val] of Object.entries(obj)) {
      const mapped = HEADER_ALIASES[normalizeHeader(rawKey)];
      if (!mapped || mapped === 'skip') continue;
      if (mapped === 'skills') {
        draft.skills = [...(draft.skills ?? []), ...parseSkillsCell(val)];
      } else if (mapped === 'full_name') {
        draft.full_name = str(val);
      } else if (mapped === 'email') {
        draft.email = str(val);
      } else if (mapped === 'phone') {
        draft.phone = str(val) || null;
      } else if (mapped === 'current_company') {
        draft.current_company = str(val) || null;
      } else if (mapped === 'current_title') {
        draft.current_title = str(val) || null;
      } else if (mapped === 'location') {
        draft.location = str(val) || null;
      } else if (mapped === 'linkedin_url') {
        draft.linkedin_url = str(val) || null;
      }
    }

    if (!draft.full_name || !draft.email) {
      parseErrors.push(`Row ${ri + 2}: missing name or email — skipped.`);
      continue;
    }
    rows.push({
      full_name: draft.full_name,
      email: draft.email,
      phone: draft.phone ?? null,
      skills: draft.skills ?? [],
      current_company: draft.current_company ?? null,
      current_title: draft.current_title ?? null,
      location: draft.location ?? null,
      linkedin_url: draft.linkedin_url ?? null,
    });
  }

  return { rows, parseErrors };
}

export function candidateImportCsvTemplate(): string {
  const h = [
    'full_name',
    'email',
    'phone',
    'skills',
    'current_company',
    'current_title',
    'location',
    'linkedin_url',
  ];
  const example = [
    'Ada Lovelace',
    'ada@example.com',
    '+91-9000000000',
    'Java,Spring,SQL',
    'ACME',
    'Senior Engineer',
    'Bengaluru',
    'https://linkedin.com/in/example',
  ];
  return `${h.join(',')}\n${example.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')}\n`;
}
